import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import type { Product } from "../../../../types/product";
import { useCart } from "../../../../context/CartContext";

interface Props {
  recentIds: string[];
  allProducts: Product[];
}

function MiniCard({ product }: { product: Product }) {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!product.inStock) return;
    addItem({
      id: product.id,
      name: product.name,
      brand: product.brand,
      price: product.price,
      image: product.image,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const saveDollars = product.originalPrice ? product.originalPrice - product.price : null;

  return (
    <div
      className="shrink-0 w-48 bg-white border border-gray-100 rounded-xl overflow-hidden hover:border-amber-300 transition-all duration-200 cursor-pointer group"
      onClick={() => {
        navigate(`/shop/${product.id}`);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }}
    >
      <div className="relative w-full h-36 bg-gray-50 overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-300"
        />
        {saveDollars && product.inStock && (
          <span className="absolute top-2 left-2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap">
            Save ${saveDollars.toLocaleString()}
          </span>
        )}
        {!product.inStock && (
          <span className="absolute top-2 left-2 bg-gray-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap">
            Sold Out
          </span>
        )}
      </div>
      <div className="p-3">
        <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-0.5 truncate">{product.brand}</p>
        <h4 className="text-xs font-semibold text-gray-900 leading-snug line-clamp-2 mb-2">{product.name}</h4>
        <div className="flex items-center justify-between gap-1">
          <span className={`font-extrabold text-sm ${product.originalPrice ? "text-red-600" : "text-gray-900"}`}>
            ${product.price.toLocaleString()}
          </span>
          <button
            onClick={handleAddToCart}
            disabled={!product.inStock}
            className={`shrink-0 px-2.5 py-1.5 rounded-md text-[10px] font-bold transition-all whitespace-nowrap cursor-pointer ${
              added
                ? "bg-emerald-500 text-white"
                : product.inStock
                ? "bg-amber-500 hover:bg-amber-400 text-white"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            {added ? (
              <span className="flex items-center gap-0.5">
                <i className="ri-check-line"></i> Done
              </span>
            ) : product.inStock ? "Add" : "—"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RecentlyViewedSection({ recentIds, allProducts }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const recentProducts = recentIds
    .map((id) => allProducts.find((p) => p.id === id))
    .filter((p): p is Product => !!p)
    .slice(0, 6);

  if (recentProducts.length === 0) return null;

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir === "right" ? 220 : -220, behavior: "smooth" });
  };

  return (
    <section className="mt-14 pt-10 border-t border-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg">
            <i className="ri-history-line text-gray-600"></i>
          </div>
          <div>
            <h2 className="text-lg font-extrabold text-gray-900 leading-tight">Recently Viewed</h2>
            <p className="text-xs text-gray-400">{recentProducts.length} product{recentProducts.length !== 1 ? "s" : ""} from your session</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => scroll("left")}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-amber-300 hover:text-amber-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <button
            onClick={() => scroll("right")}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-amber-300 hover:text-amber-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>
      </div>

      {/* Scrollable row */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 scroll-smooth"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {recentProducts.map((p) => (
          <MiniCard key={p.id} product={p} />
        ))}
      </div>
    </section>
  );
}
